import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/format';
import type { Review } from '@/types';
import StarsRating from './StarsRating';

/** Comments longer than this collapse behind a "show more" toggle. */
const COLLAPSE_LENGTH = 180;

export default function ReviewCard({ review }: { review: Review }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const isLong = Boolean(review.comment && review.comment.length > COLLAPSE_LENGTH);
  const commentText = review.comment
    ? isLong && !expanded
      ? `${review.comment.slice(0, COLLAPSE_LENGTH).trimEnd()}…`
      : review.comment
    : null;

  return (
    <article className="rounded-lg border border-background-200 bg-background-50 p-4 shadow-xs md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-user-3-line text-lg" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground-950">{review.customerName}</p>
            <p className="mt-0.5 text-xs text-foreground-400">
              {formatDate(review.createdAt, i18n.language)}
            </p>
          </div>
        </div>
        <StarsRating value={review.rating} size="md" showValue className="shrink-0" />
      </div>

      {commentText ? (
        <p className="mt-3 whitespace-pre-line break-words text-sm leading-relaxed text-foreground-700">
          {commentText}
        </p>
      ) : (
        <p className="mt-3 text-sm italic text-foreground-400">{t('reviews.card.noComment')}</p>
      )}

      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
        >
          <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" />
          {t(expanded ? 'reviews.card.showLess' : 'reviews.card.showMore')}
        </button>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-background-100 pt-3 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-file-list-3-line text-base text-foreground-400" aria-hidden="true" />
          {t('reviews.card.order')}:
          <span className="lb-ltr-field font-medium text-foreground-700">{review.orderId}</span>
        </span>
        <Link
          to={`/dashboard/orders/${review.orderId}`}
          className="inline-flex items-center gap-1 font-medium text-primary-700 transition-colors hover:text-primary-800"
        >
          <i className="ri-external-link-line" aria-hidden="true" />
          {t('reviews.card.viewOrder')}
        </Link>
      </div>
    </article>
  );
}